/**
 * Les réglages du site, côté base : lire, écrire, téléverser.
 *
 * Séparé de `index.ts` pour que la logique de repli soit testable sans
 * variables d'environnement — `lib/supabase` lève au chargement quand elles
 * manquent, ce qui rendait tout le module intestable.
 */

import { supabase } from '../supabase';
import { PAR_DEFAUT, depuisLignes, type CleReglage, type Reglages } from './index';

/**
 * Lit tous les réglages, et lève si la requête échoue.
 *
 * Deux lectures existent parce que « on ne sait pas encore » et « on n'a pas
 * pu savoir » ne se traitent pas pareil. Un écran qui n'a rien à afficher
 * attend ; un écran dont la requête a échoué doit bien montrer quelque chose.
 * Confondre les deux, c'était rendre les valeurs par défaut à tout le monde le
 * temps de chaque requête — donc l'ancienne photo, à chaque arrivée.
 */
export async function lireReglagesOuLeve(): Promise<Reglages> {
  const { data, error } = await supabase.from('site_reglages').select('cle, valeur');
  if (error) throw error;
  return depuisLignes(data as { cle: string; valeur: unknown }[] | null);
}

/**
 * Lit tous les réglages.
 *
 * Ne lève jamais : une erreur rend les valeurs par défaut. Le site vaut mieux
 * qu'une page blanche, et un réglage est par nature accessoire — s'il l'était
 * moins, il n'aurait pas sa place dans cette table.
 */
export async function lireReglages(): Promise<Reglages> {
  try {
    return await lireReglagesOuLeve();
  } catch (err) {
    console.error('Réglages du site non chargés, valeurs par défaut :', err);
    return PAR_DEFAUT;
  }
}

/** Écrit un réglage. Réservé aux administrateurs par la RLS. */
export async function ecrireReglage(cle: CleReglage, valeur: unknown, auteurId: string | null) {
  const { error } = await supabase.from('site_reglages').upsert(
    { cle, valeur, maj_le: new Date().toISOString(), maj_par: auteurId },
    { onConflict: 'cle' },
  );
  if (error) throw new Error(error.message);
}

/* -------------------------------------------------------------------------- */
/* Le téléversement                                                            */
/* -------------------------------------------------------------------------- */

export const SEAU_IMAGES = 'site-images';
export const TAILLE_MAX_OCTETS = 5 * 1024 * 1024;
export const TYPES_ACCEPTES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Envoie une image dans le seau et rend son URL publique.
 *
 * Le nom porte un horodatage : sans lui, remplacer une photo par une autre du
 * même nom laisserait les caches — navigateurs et intermédiaires — servir
 * l'ancienne pendant des heures, et l'équipe croirait le téléversement raté.
 */
export async function televerserImage(fichier: File, cle: CleReglage): Promise<string> {
  if (!TYPES_ACCEPTES.includes(fichier.type)) {
    throw new Error('Seuls les formats JPG, PNG et WebP sont acceptés.');
  }
  if (fichier.size > TAILLE_MAX_OCTETS) {
    throw new Error('L’image ne doit pas dépasser 5 Mo.');
  }

  const extension = fichier.type === 'image/png' ? 'png' : fichier.type === 'image/webp' ? 'webp' : 'jpg';
  const chemin = `${cle}-${Date.now()}.${extension}`;

  const { error } = await supabase.storage
    .from(SEAU_IMAGES)
    .upload(chemin, fichier, { contentType: fichier.type, upsert: true });
  if (error) throw new Error(error.message);

  const { data } = supabase.storage.from(SEAU_IMAGES).getPublicUrl(chemin);
  if (!data?.publicUrl) throw new Error('L’image a été envoyée mais son adresse est introuvable.');
  return data.publicUrl;
}
